import { AccountDetailResponsePayload } from '@Types'
import isNil from 'lodash.isnil'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { buildAccountText } from '../../utils/optionBuilders'
import { TransferForm } from '@Types'
import { useFormatNumber } from '../../utils'
import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { PositionJournalTransferDetails } from './PositionJournalTransferDetails'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooterAction
} from '@apex/component-library'
import { PositionJournalTransfer } from '../../api/useGetPositionJournals'
import { useCancelTransfer } from '../../api/useCancelTransfer'
import { removeUnderscores } from '../../utils'

export interface Props {
  accountNumber: string
  transferData?: PositionJournalTransfer | null
  type: string
  accountDetails?: AccountDetailResponsePayload | null
  isOpen: boolean
  onClose: () => void
}

export const CancelPositionJournalTransferModal = ({
  transferData,
  type,
  accountDetails,
  accountNumber,
  isOpen,
  onClose
}: Props) => {
  const formatNumber = useFormatNumber()
  const { mutate, isLoading } = useCancelTransfer(transferData?.id!, accountNumber)

  const cancelTransfer = () => {
    mutate(
      {},
      {
        onSettled: () => {
          onClose()
        }
      }
    )
  }

  const [details, setDetails] = useState<TransferForm>({
    transferDate: '',
    transferDetails: {
      transferType: {
        raw: '',
        display: ''
      },
      transferAmount: {
        display: '',
        raw: ''
      },
      accounts: {
        direction: '',
        from: {
          raw: '',
          display: ''
        },
        to: {
          raw: '',
          display: ''
        }
      }
    }
  })

  const buildAccounts = (
    accountNumber: string,
    transfer?: PositionJournalTransfer | null,
    account?: AccountDetailResponsePayload | null
  ) => {
    if (isNil(transfer?.direction)) {
      return {}
    }

    return {
      from:
        transferData?.positionJournalDetails?.sourceAccount! === accountNumber
          ? buildAccountText(transferData?.positionJournalDetails?.sourceAccount!, account?.accountTitle)
          : transferData?.positionJournalDetails?.sourceAccount!,
      to: transferData?.positionJournalDetails?.destinationAccount!
    }
  }

  const buildTransferType = (transfer: PositionJournalTransfer, accountNumber: string) => {
    const method = removeUnderscores(transfer?.method) || ''
    let direction

    if (accountNumber === transfer?.positionJournalDetails?.sourceAccount) {
      direction = 'Debit'
    } else {
      direction = 'Credit'
    }

    return `${method} ${direction}`
  }

  const buildTransferDetails = () => {
    const submittedDate = transferData?.initiationTime
      ? DateTime.fromISO(transferData?.initiationTime).toLocaleString(DateTime.DATE_SHORT)
      : ''

    const amount =
      isNil(transferData?.amount) || isNaN(transferData?.amount as number)
        ? ''
        : formatNumber(transferData?.amount as number, {
            style: 'currency',
            currency: 'USD'
          })
    const data: TransferForm = {
      transferDate: submittedDate,
      transferDetails: {
        transferType: {
          raw: type,
          display: buildTransferType(transferData!, accountNumber)
        },
        transferAmount: {
          display: amount,
          raw: transferData?.amount as number
        },
        positionJournalDetails: transferData?.positionJournalDetails,
        accounts: buildAccounts(accountNumber, transferData, accountDetails) as any
      }
    }
    setDetails(data)
  }

  useEffect(() => {
    buildTransferDetails()
    // eslint-disable-next-line
  }, [accountDetails, transferData])

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay>
        <ModalContent w='100%' maxW='600px'>
          <ModalHeader textStyle='h1'>Cancel Transfer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack pb={8} spacing={8}>
              <Text textStyle='lead'>Please confirm you want to cancel the following transfer.</Text>
              <Text textStyle='h5'>Transfer Details </Text>
              <PositionJournalTransferDetails fields={details} />
            </Stack>
          </ModalBody>
          <ModalFooterAction>
            <Flex>
              <Box ml='auto'>
                <Button onClick={onClose} variant='white'>
                  Close
                </Button>
                <Button variant='negative' ml={3} onClick={cancelTransfer} isLoading={isLoading} isDisabled={isLoading}>
                  Cancel Transfer
                </Button>
              </Box>
            </Flex>
          </ModalFooterAction>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
